import { z } from 'zod'
import './registry'
import {
  TenantIdSchema,
  UlidSchema,
  ShortIdSchema,
  IsoDateSchema,
  MoneySchema,
  CurrencySchema,
} from './common'
import { RecordChannelSchema, RecordConditionSchema, RecordSchema, SlotIdSchema } from './records'

/**
 * Point-of-Sale domain schemas — the in-store till.
 *
 * A `Transaction` is one completed counter sale: a basket of records (plus any
 * non-catalogued "quick" lines) flipped to `sold` in a single atomic
 * DynamoDB `TransactWriteItems`. The data model was pre-seeded for this in
 * `lib/ddb.ts` (`txnSk`/`txnItemSk`/`idempotencySk`, the GSI5 TxnByDate index)
 * and `lib/audit.ts` (`SALE_COMPLETED`/`RETURN_PROCESSED`).
 *
 * Scope notes (decisions locked with the shop owner, 2026-06-25):
 *   - VAT: we CAPTURE a per-line `vatTreatment` flag (margin scheme vs standard
 *     21%) but do NOT compute or settle VAT here — second-hand records likely
 *     fall under the margeregeling, an accountant decision deferred to Phase 4
 *     reporting. The flag is stored now so that reporting has the data.
 *   - Payments stay external (the physical pin terminal moves the money). We only
 *     RECORD the method (+ cash tendered/change) so the day reconciles.
 *   - Reservations: a record is reserved the moment it enters a basket (see the
 *     reserve endpoint) and only flips to `sold` at checkout, so an abandoned
 *     basket never strands a record without its slot.
 */

/**
 * How the customer paid. The terminal processes the money; we only record this.
 * `online` is the webshop's settlement (Mollie) — written by the paid webhook's
 * transaction mirror, never selectable at the till. What staff CAN pick is
 * `TillPaymentMethodSchema` below.
 */
export const PaymentMethodSchema = z
  .enum(['pin', 'cash', 'split', 'online'])
  .openapi('PaymentMethod')
export type PaymentMethod = z.infer<typeof PaymentMethodSchema>

/**
 * What a cashier can actually ring up. DERIVED from the full set rather than
 * spelled out a second time: the next settlement channel added above (a second
 * PSP, 'invoice', 'giftcard') then has to be excluded here on purpose, instead
 * of silently widening the till. Accepting `online` at the till would let a
 * till sale masquerade as a Mollie one in Rapportages' payment column and drop
 * out of that day's cash/PIN reconciliation. The POS store types its selection
 * off this schema, so server and picker cannot drift.
 */
export const TillPaymentMethodSchema = PaymentMethodSchema.exclude(['online'])
export type TillPaymentMethod = z.infer<typeof TillPaymentMethodSchema>

/**
 * VAT treatment of a single line, CAPTURED not computed:
 *   margin   — second-hand goods under the margeregeling; VAT is on the dealer
 *              margin and never shown separately to the customer.
 *   standard — standard-rated 21% (new/sealed stock, accessories, quick items).
 * The actual VAT calculation + receipt wording is Phase-4 work, pending the
 * shop's accountant. Here it's only recorded per line for that later reporting.
 *
 * For catalogued lines, `standard` vs `margin` is derived from the record's
 * `isNew` flag (see RecordSchema field doc), which is immutable once set. One
 * consequence worth flagging for that Phase-4 decision: a sold-new copy that's
 * later returned and re-stocked keeps `isNew: true`, so it still sells
 * `standard` on its next sale even though it's now technically second-hand —
 * intentional under the "immutable at intake" rule, not a bug.
 */
export const VatTreatmentSchema = z.enum(['margin', 'standard']).openapi('VatTreatment')
export type VatTreatment = z.infer<typeof VatTreatmentSchema>

/**
 * Why a discount was given — captured optionally at sale time so the owner can
 * later see patterns per staff member (see `discountReason` on `TransactionLine`
 * and `basketDiscountReason` on `Transaction`). `other` pairs with a free-text
 * `discountReasonNote`/`basketDiscountReasonNote`; the other values are terse
 * enough to stand alone.
 */
export const DiscountReasonSchema = z
  .enum(['damaged', 'goodwill', 'price_match', 'staff_error', 'other'])
  .openapi('DiscountReason')
export type DiscountReason = z.infer<typeof DiscountReasonSchema>

/**
 * Request-side boundary hardening: a note only makes sense attached to a
 * reason (any of the five — not just `'other'`; `useDiscountReasonHint` and
 * `ReportsPayouts.vue`'s `reasonLabel` both render `"<label>: <note>"` for
 * every reason, not only `'other'`). Nothing in the field types stops a
 * non-standard client from sending a note with NO reason at all, which every
 * display path then drops silently (they gate on `!reason`, ignoring a
 * present note). The frontend never produces this shape — `discountReasonFields`
 * omits the note whenever reason is unset — so this is the same category as
 * `CreateSaleRequestSchema`'s duplicate-`recordId` `.superRefine`: a
 * malformed-request guard, not something the shipping client can trigger.
 */
const noteRequiresAReason = (
  v: { discountReason?: DiscountReason | undefined; discountReasonNote?: string | undefined },
  ctx: z.RefinementCtx,
  noteField: 'discountReasonNote' | 'basketDiscountReasonNote' = 'discountReasonNote'
): void => {
  if (v.discountReasonNote !== undefined && v.discountReason === undefined) {
    ctx.addIssue({
      code: 'custom',
      message: `${noteField} requires a discountReason to be set`,
      path: [noteField],
    })
  }
}

/** Lifecycle of a transaction. `reversed` is reserved for the admin-only reversal flow (later). */
export const TransactionStatusSchema = z
  .enum(['completed', 'reversed'])
  .openapi('TransactionStatus')
export type TransactionStatus = z.infer<typeof TransactionStatusSchema>

/**
 * One line on a completed sale. Snapshots everything reporting/payouts will need
 * so historical transactions stay correct even if the record or supplier config
 * later changes: price, discount, VAT flag, and the supplier share at sale time
 * (the record already carries `supplierShareSnapshot` from intake — we copy it
 * onto the line so a transaction is self-contained).
 *
 * `recordId` is null for a non-catalogued "quick" line (a bag, a coffee, an
 * un-catalogued record); catalogued lines carry the record id + its short id and
 * the channel the copy sold through.
 */
export const TransactionLineSchema = z
  .object({
    recordId: UlidSchema.nullable(),
    shortId: ShortIdSchema.nullable(),
    description: z.string().min(1).max(300),
    condition: RecordConditionSchema.nullable(),
    /** The sales channel the copy sold through (store/online/other), null for quick lines. */
    channelAtSale: RecordChannelSchema.nullable(),
    unitPriceEur: MoneySchema,
    discountEur: MoneySchema,
    /**
     * Why this line's discount was given (optional — captured on a
     * best-effort basis at the till). `.default(null)`, not just
     * `.nullable()`, because this field is newer than the line itself:
     * transactions persisted before it existed have no such key in DynamoDB
     * at all, and a bare `.nullable()` would fail to parse them (a missing
     * key is `undefined`, not `null`) — same class of fix as
     * `Transaction.reversalReason` below.
     */
    discountReason: DiscountReasonSchema.nullable().default(null),
    /** Free-text detail; only meaningful when `discountReason === 'other'`. */
    discountReasonNote: z.string().min(1).max(300).nullable().default(null),
    /** unitPriceEur − discountEur, snapshotted so the line is self-contained. */
    lineTotalEur: MoneySchema,
    vatTreatment: VatTreatmentSchema,
    /**
     * The tenant's standard VAT rate at sale time, snapshotted for the same
     * reason as `supplierShareSnapshot` below: a later settings edit (or a
     * statutory rate change) must not silently re-base what a historical
     * payout owed. Only meaningful when `vatTreatment === 'standard'`, but
     * stamped on every line so the line stays self-contained.
     *
     * `.default(null)` rather than a bare `.nullable()` for the same reason as
     * `discountReason` above: lines persisted before this field existed carry
     * no such key in DynamoDB at all, and `undefined` fails a bare
     * `.nullable()`. Those fall back to the live tenant rate — see
     * `payoutBasisCents` in handlers/reports.ts.
     */
    vatRatePercent: z.number().min(0).max(100).nullable().default(null),
    supplierId: UlidSchema.nullable(),
    /** Supplier revenue-share % at sale time (copied from the record's intake snapshot). */
    supplierShareSnapshot: z.number().min(0).max(100).nullable(),
  })
  .openapi('TransactionLine')
export type TransactionLine = z.infer<typeof TransactionLineSchema>

/**
 * Wire-shape of a completed sale. Like RecordSchema this is non-strict: the
 * persisted DDB item also carries PK/SK/gsi5pk/gsi5sk which zod strips on parse.
 */
export const TransactionSchema = z
  .object({
    id: UlidSchema,
    tenantId: TenantIdSchema,
    status: TransactionStatusSchema,
    /** Cognito sub of the assistant who rang the sale — stamped from the JWT, never the client. */
    cashierId: z.string(),
    /**
     * Which physical till. Reserved for a future multi-register setup; always null
     * in v1 (Toon runs a single iPad till). Kept on the model so adding a second
     * register later is additive, not a migration.
     */
    registerId: z.string().nullable(),
    lines: z.array(TransactionLineSchema),
    paymentMethod: PaymentMethodSchema,
    /** Sum of line totals before the basket-level discount. */
    subtotalEur: MoneySchema,
    /** Whole-basket discount applied on top of any per-line discounts. */
    basketDiscountEur: MoneySchema,
    /** Why the basket discount was given — see `TransactionLine.discountReason`. */
    basketDiscountReason: DiscountReasonSchema.nullable().default(null),
    /** Free-text detail; only meaningful when `basketDiscountReason === 'other'`. */
    basketDiscountReasonNote: z.string().min(1).max(300).nullable().default(null),
    /** subtotalEur − basketDiscountEur — the amount actually charged. */
    totalEur: MoneySchema,
    currency: CurrencySchema,
    /** Cash handed over (cash/split payments), else null. Recorded, not processed. */
    cashTenderedEur: MoneySchema.nullable(),
    /** Change returned to the customer (derived from cashTendered − total), else null. */
    changeGivenEur: MoneySchema.nullable(),
    note: z.string().max(500).nullable(),
    createdAt: IsoDateSchema,
    /** Set only when an admin later reverses this sale; null otherwise. */
    reversedAt: z.union([IsoDateSchema, z.null()]),
    reversedBy: z.string().nullable(),
    /**
     * Why an admin reversed this sale (required input on the reversal request
     * itself). `.default(null)` — NOT just `.nullable()` — because this field is
     * newer than `reversedAt`/`reversedBy`: transactions already persisted before
     * this field existed have no such key in DynamoDB at all, and a bare
     * `.nullable()` would fail to parse them (a missing key is `undefined`, not
     * `null`). Same class of bug as `TenantSettingsSchema`'s VAT rate fields.
     */
    reversalReason: z.string().min(1).max(500).nullable().default(null),
    /**
     * Set once RV has recorded a refund against this sale (lib/order-refund.ts).
     *
     * A refund is accounted for in EXACTLY ONE of two ways, never both:
     *   • Return rows — each carries `refundedEur`, which salesReport subtracts
     *     and payoutsReport claws back. Partial-capable.
     *   • Reversal — `status: 'reversed'`, which both reports exclude WHOLE.
     *
     * Applying both to one sale subtracts the money twice and drives revenue
     * negative. So this flag makes the choice sticky: `reverseOrderSaleTransaction`
     * refuses to reverse a sale that already carries Return rows, and the refund
     * handler refuses to add Return rows to a sale already reversed.
     *
     * `.default(false)` for every transaction written before this field existed.
     */
    refundRecorded: z.boolean().default(false),
  })
  .openapi('Transaction')
export type Transaction = z.infer<typeof TransactionSchema>

// ── Reservations ──────────────────────────────────────────────────────

/**
 * Reserve a single record into a POS basket. A record is identified by either its
 * ULID (search-to-add) or its 6-digit short id (scan/type) — at least one is
 * required. The handler does a conditional write guarding `status === in_stock`,
 * flips it to `reserved`, stamps the holding session + an expiry, and delists it
 * from Discogs (anti-oversell). `sessionId` identifies the basket so only the same
 * basket can release or sell the hold.
 */
export const ReserveRecordRequestSchema = z
  .object({
    /**
     * Correlates this reserve attempt across logs/traces — NOT a formal
     * idempotency key (unlike sale/processReturn, no requestId-keyed marker is
     * written or checked). A retry needs no such marker: reserving an
     * already-`reserved`-by-this-`sessionId` record is itself a no-op
     * (handlers/pos.ts `reserve`), so re-sends are safe regardless of whether
     * requestId matches. Reused across retries only for traceability, not to
     * de-duplicate a write.
     */
    requestId: UlidSchema,
    /** Opaque per-basket id minted by the till client; ties the hold to this basket. */
    sessionId: z.string().min(1).max(64),
    recordId: UlidSchema.optional(),
    shortId: ShortIdSchema.optional(),
  })
  .strict()
  .refine(v => v.recordId !== undefined || v.shortId !== undefined, {
    message: 'recordId or shortId is required',
  })
  .openapi('ReserveRecordRequest')
export type ReserveRecordRequest = z.infer<typeof ReserveRecordRequestSchema>

/**
 * Release a held record back to stock (the assistant removed it from the basket,
 * or it was abandoned). Guards that the caller's `sessionId` owns the hold, flips
 * `reserved → in_stock`, and re-lists it on Discogs if it was listed before. The
 * record id comes from the path.
 */
export const ReleaseReservationRequestSchema = z
  .object({
    sessionId: z.string().min(1).max(64),
  })
  .strict()
  .openapi('ReleaseReservationRequest')
export type ReleaseReservationRequest = z.infer<typeof ReleaseReservationRequestSchema>

// ── Sale ──────────────────────────────────────────────────────────────

/**
 * A catalogued line: just the record id + an optional per-line discount. The
 * server reads the record (which must be reserved by this basket) for the price,
 * description, condition, channel and supplier snapshot — the client never sets
 * those, so a tampered price can't get through.
 */
const CataloguedSaleLineSchema = z.object({
  kind: z.literal('catalogued'),
  recordId: UlidSchema,
  discountEur: MoneySchema.default('0.00'),
  /** Optional — why this line's discount was given, for owner visibility. */
  discountReason: DiscountReasonSchema.optional(),
  discountReasonNote: z.string().min(1).max(300).optional(),
})

/**
 * A non-catalogued "quick" line: free description + manually-typed price + VAT
 * flag. For accessories, a coffee, or an un-catalogued record, so the till total
 * can actually reconcile with the drawer.
 */
const QuickSaleLineSchema = z.object({
  kind: z.literal('quick'),
  description: z.string().min(1).max(300),
  unitPriceEur: MoneySchema,
  vatTreatment: VatTreatmentSchema.default('standard'),
  discountEur: MoneySchema.default('0.00'),
  /** Optional — why this line's discount was given, for owner visibility. */
  discountReason: DiscountReasonSchema.optional(),
  discountReasonNote: z.string().min(1).max(300).optional(),
})

export const SaleLineInputSchema = z
  .discriminatedUnion('kind', [CataloguedSaleLineSchema, QuickSaleLineSchema])
  .superRefine(noteRequiresAReason)
  .openapi('SaleLineInput')
export type SaleLineInput = z.infer<typeof SaleLineInputSchema>

/**
 * Complete a sale: flip every catalogued line's record to `sold` and write the
 * Transaction in one atomic `TransactWriteItems` (all-or-nothing — no
 * half-sold basket on a failure). The records must currently be `reserved` by
 * `sessionId`; a record that slipped away (sold elsewhere, hold expired) fails the
 * conditional write and the whole sale is rejected so the assistant can re-check.
 */
export const CreateSaleRequestSchema = z
  .object({
    /**
     * Idempotency key (one ULID per logical sale, reused across retries). The sale
     * is the money-critical write; a double-tap or client retry must never create
     * two transactions or sell twice. The handler replays the stored transaction
     * for a repeated requestId.
     */
    requestId: UlidSchema,
    /** The basket session whose reservations this sale settles. */
    sessionId: z.string().min(1).max(64),
    // Capped well under DynamoDB's 100-item TransactWriteItems limit: the atomic
    // sale writes the transaction header + an idempotency marker + one flip per
    // catalogued line, so N + 2 must stay ≤ 100. 50 is far more than any real
    // counter basket while leaving comfortable headroom.
    lines: z.array(SaleLineInputSchema).min(1).max(50),
    /** Whole-basket discount on top of any per-line discounts. */
    basketDiscountEur: MoneySchema.default('0.00'),
    /** Optional — why the basket discount was given, for owner visibility. */
    basketDiscountReason: DiscountReasonSchema.optional(),
    basketDiscountReasonNote: z.string().min(1).max(300).optional(),
    // NOT the full PaymentMethodSchema — see TillPaymentMethodSchema's note.
    paymentMethod: TillPaymentMethodSchema,
    /** Cash handed over (cash/split), recorded for the change calc + reconciliation. */
    cashTenderedEur: MoneySchema.optional(),
    note: z.string().max(500).optional(),
  })
  .strict()
  // Two catalogued lines sharing one recordId would each queue an `Update` on the
  // SAME item within one TransactWriteItems call, which DynamoDB rejects outright
  // (not a conditional-check failure — a hard ValidationException) — surfacing as
  // an opaque 500 instead of a clean 4xx. The UI already dedupes by record.id, so
  // this can't happen via normal use; this is a boundary hardening against a
  // malformed/duplicate request, same category as ReserveRecordRequestSchema's
  // recordId-or-shortId `.refine()` above.
  .superRefine((v, ctx) => {
    const seen = new Set<string>()
    for (const line of v.lines) {
      if (line.kind !== 'catalogued') continue
      if (seen.has(line.recordId)) {
        ctx.addIssue({
          code: 'custom',
          message: `Duplicate recordId in one sale basket: ${line.recordId}`,
          path: ['lines'],
        })
        return
      }
      seen.add(line.recordId)
    }
  })
  .superRefine((v, ctx) =>
    noteRequiresAReason(
      { discountReason: v.basketDiscountReason, discountReasonNote: v.basketDiscountReasonNote },
      ctx,
      'basketDiscountReasonNote'
    )
  )
  .openapi('CreateSaleRequest')
export type CreateSaleRequest = z.infer<typeof CreateSaleRequestSchema>

// ── Receipt e-mail ────────────────────────────────────────────────────

/**
 * POST /pos/sales/{id}/email body — send the kassabon to a customer address.
 * The client sends ONLY the destination; the bon content is derived
 * server-side from the stored transaction + default supplier + settings, so
 * a tampered client can never send arbitrary content in the shop's name.
 * Deliberately NOT idempotency-keyed (unlike sale/processReturn): a
 * duplicate e-mail is an annoyance, not a money event — the UI guards
 * double-taps.
 */
export const EmailReceiptRequestSchema = z
  .object({
    email: z.string().email().max(254),
  })
  .strict()
  .openapi('EmailReceiptRequest')
export type EmailReceiptRequest = z.infer<typeof EmailReceiptRequestSchema>

export const EmailReceiptResponseSchema = z
  .object({
    status: z.literal('sent'),
  })
  .openapi('EmailReceiptResponse')
export type EmailReceiptResponse = z.infer<typeof EmailReceiptResponseSchema>

// ── Returns ───────────────────────────────────────────────────────────

/**
 * Why money for a record went back — for returns reporting. `lost_in_transit`
 * only ever comes from an ORDER refund (a parcel lost in the post: the copy
 * never reached the customer and never comes back); the returns desk at the
 * till has a customer and a copy in front of it, so it uses
 * TillReturnReasonSchema, which leaves that one out.
 */
export const ReturnReasonSchema = z
  .enum(['defect', 'changed_mind', 'wrong_item', 'lost_in_transit'])
  .openapi('ReturnReason')
export type ReturnReason = z.infer<typeof ReturnReasonSchema>

/** The reasons the till's returns desk can record (no "lost in the post" there). */
export const TillReturnReasonSchema = ReturnReasonSchema.exclude(['lost_in_transit']).openapi(
  'TillReturnReason'
)
export type TillReturnReason = z.infer<typeof TillReturnReasonSchema>

/**
 * How the refund was given. We only ever RECORD this — no value here moves
 * money by itself:
 *
 *   pin | cash  the terminal at the returns desk moved it
 *   online      our own PSP moved it (a webshop order refunded through Mollie)
 *   external    someone moved it outside this system — a Discogs order, where
 *               the seller refunds in Discogs' own UI because their API has no
 *               refund endpoint at all. RV records the amount so the books stay
 *               right; the money left via PayPal.
 */
export const RefundMethodSchema = z
  .enum(['pin', 'cash', 'online', 'external'])
  .openapi('RefundMethod')
export type RefundMethod = z.infer<typeof RefundMethodSchema>

/**
 * Where a returned copy goes back to. A subset of the sales channels — a return
 * always re-enters sellable stock, so `not_for_sale`/`scrapped` aren't offered
 * (those are reached via the catalogue's discard flow, not the returns desk).
 * Only `online` gets a back-storage slot (mirrors intake: SLOT_CHANNELS).
 */
export const RestockChannelSchema = z.enum(['store', 'online', 'other']).openapi('RestockChannel')
export type RestockChannel = z.infer<typeof RestockChannelSchema>

/**
 * A processed return: one copy taken back and re-stocked. Immutable record of the
 * event (who/when/why/how-much), linked to the original sale when known. Persisted
 * at `RETURN#<id>` (the key builder + the `RETURN_PROCESSED` audit action already
 * exist). `refundedEur` is what the customer paid (the original line total, or the
 * record's sale price when the original transaction isn't known); `repricedEur` is
 * the new shelf price the re-graded copy goes back out at.
 *
 * `supplierId`/`supplierShareSnapshot`/`lineTotalEur`/`shortId`/`description`
 * snapshot the ORIGINAL sale's matching line (same fields `TransactionLine`
 * itself snapshots at sale time) so the payouts report can claw back a
 * since-returned sale from a supplier's total without joining back to the
 * original transaction. Null together whenever the original line isn't
 * identifiable (same conditions as a `refundedEur` fallback: no sale link, an
 * unparseable linked transaction, or a linked transaction that doesn't list
 * this recordId) — a null `supplierId` means this return contributes no
 * payout clawback at all.
 *
 * All five are `.nullable().default(null)`, NOT bare `.nullable()`: a Return
 * persisted before these fields existed has no such key in DynamoDB at all,
 * and a missing key parses as `undefined`, not `null` — bare `.nullable()`
 * would reject it. Same class of bug as `Transaction.reversalReason` above.
 */
export const ReturnSchema = z
  .object({
    id: UlidSchema,
    tenantId: TenantIdSchema,
    /**
     * The copy that came back, or null when the refund was not about a copy at
     * all — refunded SHIPPING on an order is money returned with no record
     * attached. Revenue subtraction counts every return by `refundedEur` and
     * needs no id (handlers/reports.ts); the payout clawback already skips a
     * return with no `supplierId`, which a shipping refund never has.
     */
    recordId: UlidSchema.nullable(),
    /**
     * The sale this copy came from, when identifiable (null if the receipt/link is
     * unknown) — always the record's OWN `saleTransactionId` at the moment of return,
     * never client-supplied: a client-suppliable link would let a stale/crafted id
     * pull in a DIFFERENT past sale of this same physical copy (recordId is stable
     * across resell cycles), silently mis-deriving the refund from the wrong sale.
     */
    originalTransactionId: UlidSchema.nullable(),
    reason: ReturnReasonSchema,
    refundMethod: RefundMethodSchema,
    refundedEur: MoneySchema,
    /**
     * Where the copy went back to — null when nothing went back into stock.
     *
     * Two cases. A refund of the SHIPPING on an order is money handed back that
     * belongs to no record (`recordId` is null too, see above). And a copy
     * refunded WITHOUT a restock (the customer keeps it, it was lost in the
     * post, or staff chose not to) has its `recordId` but no restock: no grade
     * change, no price, no slot. These fields carry null in both.
     * `.default(null)` for rows written before a copy-less return was possible,
     * which all had real values.
     */
    restockChannel: RestockChannelSchema.nullable().default(null),
    /** New back-storage slot if it went back online, else null. */
    newLocationId: SlotIdSchema,
    /** Condition the copy was re-graded to on restock (it may have been opened/played). */
    regradeCondition: RecordConditionSchema.nullable().default(null),
    repricedEur: MoneySchema.nullable().default(null),
    cashierId: z.string(),
    createdAt: IsoDateSchema,
    /** The supplier owed for the original sale, or null — see doc above. */
    supplierId: UlidSchema.nullable().default(null),
    /** Revenue-share % locked at the ORIGINAL sale time, not the supplier's current config. */
    supplierShareSnapshot: z.number().min(0).max(100).nullable().default(null),
    /**
     * The payout basis to claw back: the original sale line's total (pre-return)
     * when the copy came back, or just the refunded amount when it did not (the
     * sale still stands for the rest; lib/record-return.ts buildRefundOnlyReturn).
     */
    lineTotalEur: MoneySchema.nullable().default(null),
    /**
     * VAT treatment of the ORIGINAL sale line, so the payout clawback can
     * mirror the sale's ex-VAT payout basis for standard-rated (new) stock —
     * see `payoutBasisCents` in handlers/reports.ts. Null on returns written
     * before this field existed: those claw back gross, matching how their
     * sale was credited under the pre-VAT-aware payout formula.
     */
    vatTreatment: VatTreatmentSchema.nullable().default(null),
    /**
     * The standard VAT rate locked at the ORIGINAL sale, copied off that sale's
     * line so a clawback is computed on exactly the basis the sale was credited
     * under — even if the tenant's rate changed in between. Null on returns
     * whose sale line predates the snapshot (or on returns written before this
     * field existed); those fall back to the live rate.
     */
    vatRatePercent: z.number().min(0).max(100).nullable().default(null),
    shortId: ShortIdSchema.nullable().default(null),
    description: z.string().min(1).max(300).nullable().default(null),
  })
  .openapi('Return')
export type Return = z.infer<typeof ReturnSchema>

/**
 * Process a return: take a sold copy back and re-stock it. The handler transitions
 * the record back to `in_stock` under `restockChannel`, applies the re-grade +
 * re-price, allocates a slot + re-lists on Discogs when it goes back online, and
 * writes the immutable `Return` record. `requestId` keys idempotency (the restock
 * allocates a slot — a retry must not allocate a second).
 */
export const ProcessReturnRequestSchema = z
  .object({
    requestId: UlidSchema,
    recordId: UlidSchema,
    reason: TillReturnReasonSchema,
    refundMethod: RefundMethodSchema,
    restockChannel: RestockChannelSchema,
    /** Re-list on Discogs when restocking online. Ignored for store/other. */
    relistOnDiscogs: z.boolean().default(false),
    newCondition: RecordConditionSchema,
    newPriceEur: MoneySchema,
  })
  .strict()
  .openapi('ProcessReturnRequest')
export type ProcessReturnRequest = z.infer<typeof ProcessReturnRequestSchema>

/**
 * Result of looking a sold copy up for return (by its 6-digit code): the record
 * plus the sale it belongs to, when linked. `transaction.lines` ARE the sibling
 * copies from the same sale, so the desk can pick the right physical record.
 * `transaction` is null when the copy carries no `saleTransactionId` (e.g. sold
 * before this flow existed, or via an online order rather than the till).
 */
export const ReturnLookupResponseSchema = z
  .object({
    record: RecordSchema,
    transaction: TransactionSchema.nullable(),
  })
  .openapi('ReturnLookupResponse')
export type ReturnLookupResponse = z.infer<typeof ReturnLookupResponseSchema>

/** Result of processing a return: the re-stocked record + the immutable Return record. */
export const ProcessReturnResponseSchema = z
  .object({
    record: RecordSchema,
    returnEntity: ReturnSchema,
  })
  .openapi('ProcessReturnResponse')
export type ProcessReturnResponse = z.infer<typeof ProcessReturnResponseSchema>

// ── Reversal ──────────────────────────────────────────────────────────

/**
 * Admin-only: undo a completed sale (a till mistake — wrong item rung up, wrong
 * price, duplicate scan). Unlike a return, this does NOT re-grade, re-price, or
 * let the assistant choose a restock channel — every catalogued line's record
 * goes back to EXACTLY its pre-sale state (same condition, price, channel). Only
 * `status: 'completed'` transactions are reversible, and only while every line's
 * record is still plain `sold` (not `sold_online`/`fulfilled` — those only ever
 * come from the separate online-order flow, never from a POS sale, so a
 * POS-sold record is always `sold`) with its `saleTransactionId` still pointing
 * at this transaction. `requestId` keys idempotency the same way
 * `CreateSaleRequestSchema.requestId` does — a retry must not re-run the record
 * flips or re-allocate a slot.
 */
export const ReverseSaleRequestSchema = z
  .object({
    requestId: UlidSchema,
    // `.trim()` before `.min(1)` so a whitespace-only reason can't slip a blank
    // "why" onto a money-affecting audit record.
    reason: z.string().trim().min(1).max(500),
  })
  .strict()
  .openapi('ReverseSaleRequest')
export type ReverseSaleRequest = z.infer<typeof ReverseSaleRequestSchema>
